import React, { useEffect, useState } from "react";
import { Box, TextField, MenuItem, Button, Typography, Paper, Stack, CircularProgress } from "@mui/material";
import QuestionnaireQuestionBuilder from "../components/QuestionnaireQuestionBuilder";
import { useNavigate } from "react-router-dom";
import { routes } from "../routes/routes";
import { useQuestionnaireBuilderStore } from "../store/useQuestionnaireBuilderStore";
import UseFetchQuestionnaireTypes from "../hooks/useFetchQuestionnaireTypes";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import useCreateQuestionnaire from "../hooks/useCreateQuestionnaire";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import useUploadExcelForQuestionnaires from "../hooks/useUploadExcelForQuestionnaires";
import BulkLoadModal from "../components/Modal/BulkLoadModal";
import BulkLoadResultsModal from "../components/Modal/BulkLoadResultsModal";
import DescriptionIcon from "@mui/icons-material/Description";
import { parseBackendErrorMessage } from "../utils/parseBackendError";
import useUploadExcelBulkQuestionnaires from "../hooks/useUploadExcelBulkQuestionnaires";

const QuestionnaireCreatePage = () => {
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [showExcelQuestionBankModal, setShowExcelQuestionBankModal] = useState(false);
  const [showImportResultModal, setShowImportResultModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const { uploadExcel, error: uploadError } = useUploadExcelForQuestionnaires();
  const [openSuccess, setOpenSuccess] = useState(false);
  const { createQuestionnaire, error: createError, loading: loadingCreateQuestionnaire } = useCreateQuestionnaire();
  const { data, loading, error } = UseFetchQuestionnaireTypes();
  const [openError, setOpenError] = React.useState(false);
  const [jsonError, setJsonError] = React.useState(null);
  const navigate = useNavigate();
  const { questionnaireData, setQuestionnaireData, resetForm } = useQuestionnaireBuilderStore();
  const [errorMessage, setErrorMessage] = useState("");

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkResultModal, setShowBulkResultModal] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const { uploadBulkExcel, loading: loadingBulk } = useUploadExcelBulkQuestionnaires();
  const TEMPLATE_BULK_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_cuestionarios_lote.xlsx`;

  const TEMPLATE_QUESTIONNAIRE_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_cuestionario_plantilla.xlsx`;
  const TEMPLATE_QUESTIONNAIRE_BANK_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_cuestionario_banco_plantilla.xlsx`;

  const handleChange = (e) => {
    setQuestionnaireData({ ...questionnaireData, [e.target.name]: e.target.value });
  };

  const handleQuestionnaireJson = (json) => {
    setQuestionnaireData({ ...questionnaireData, questionnaire_json: json });
  };

  // Envía los datos del formulario para crear un nuevo cuestionario
  const handleSubmit = async () => {
    const payload = {
      external_id: questionnaireData.external_id,
      title: questionnaireData.title,
      scope: questionnaireData.scope,
      instructions: `<p>${(questionnaireData.instructions || "").trim()}</p>`,
      questionnaire_type: questionnaireData.questionnaire_type,
      content: questionnaireData.questionnaire_json?.content,
    };

    const success = await createQuestionnaire(payload);

    if (success) {
      resetForm();
      setOpenSuccess(true);
    } else {
      setOpenError(true);
    }
  };

  // Valida que el formulario esté completo y correcto antes de permitir envío
  const isFormValid = () => {
    const requiredFields = ["external_id", "title", "instructions", "scope"];
    const allTextFieldsFilled = requiredFields.every((field) => (questionnaireData[field] || "").trim() !== "");
    const dropdownsValid = questionnaireData.questionnaire_type !== undefined;
    const hasQuestions =
      Array.isArray(questionnaireData.questionnaire_json?.content?.pages?.[0]?.elements) &&
      questionnaireData.questionnaire_json.content.pages[0].elements.length > 0;
    const jsonIsValid = !jsonError;

    return allTextFieldsFilled && dropdownsValid && hasQuestions && jsonIsValid;
  };

  useEffect(() => {
    const backendError = createError || uploadError || error;
    if (backendError) {
      setErrorMessage(backendError.message || "Ocurrió un error al procesar la solicitud.");
      setOpenError(true);
    }
  }, [createError, uploadError, error]);

  return (
    <Box sx={{ p: 4, maxWidth: 1000, mx: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mb: 2 }}>
        <Box>
          <Button variant="contained" onClick={() => setShowExcelModal(true)} sx={{ minHeight: 42 }}>
            Crear cuestionario por Excel
          </Button>
        </Box>
        <Box>
          <Button variant="contained" onClick={() => setShowExcelQuestionBankModal(true)} sx={{ minHeight: 42 }}>
            Crear cuestionario por Excel con preguntas de banco
          </Button>
        </Box>
        <Box>
          <Button variant="contained" onClick={() => setShowBulkModal(true)} sx={{ minHeight: 42 }}>
            Carga en lote de cuestionarios (banco de preguntas)
          </Button>
        </Box>
      </Box>
      <Typography gutterBottom sx={{ fontWeight: 600, color: "#333333", fontSize: "1.8rem", mb: 4 }}>
        Información del cuestionario
      </Typography>

      {/* Formulario de campos básicos */}
      <Paper sx={{ p: 3, mb: 4 }} elevation={3}>
        <Stack spacing={2}>
          <TextField label="ID externo del cuestionario" name="external_id" value={questionnaireData.external_id} onChange={handleChange} fullWidth />
          <TextField label="Título" name="title" value={questionnaireData.title} onChange={handleChange} fullWidth />
          <TextField
            label="Instrucciones"
            name="instructions"
            value={questionnaireData.instructions}
            onChange={handleChange}
            fullWidth
            multiline
            rows={4}
          />
          <TextField label="Alcance" name="scope" value={questionnaireData.scope} onChange={handleChange} fullWidth />
          <TextField
            select
            label="Tipo de cuestionario"
            name="questionnaire_type"
            value={
              data?.questionnaire_types?.some((type) => type.id === questionnaireData.questionnaire_type) ? questionnaireData.questionnaire_type : ""
            }
            onChange={handleChange}
            fullWidth
            disabled={loading || !data?.questionnaire_types}
          >
            {(data?.questionnaire_types || []).map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <QuestionnaireQuestionBuilder
        externalId={questionnaireData.external_id}
        title={questionnaireData.title}
        scope={questionnaireData.scope}
        instructions={questionnaireData.instructions}
        onQuestionnaireGenerated={handleQuestionnaireJson}
        jsonError={jsonError}
        setJsonError={setJsonError}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", mt: 4 }}>
        <Box sx={{ justifySelf: "start", alignSelf: "start" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!isFormValid() || loadingCreateQuestionnaire}
            startIcon={loadingCreateQuestionnaire ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minHeight: 42 }}
          >
            {loadingCreateQuestionnaire ? "Creando..." : "Crear cuestionario"}
          </Button>
          {!isFormValid() && <Typography sx={{ color: "#b65858", mt: 1 }}>Debe completar toda la información del cuestionario</Typography>}
        </Box>
        <Box sx={{ justifySelf: "end", alignSelf: "end", mt: 2, gridColumn: 3 }}>
          <Button variant="contained" color="primary" onClick={() => navigate(routes.cuestionarios)} sx={{ minHeight: 42 }}>
            Regresar
          </Button>
        </Box>
      </Box>

      <ErrorSnackbar open={openError} onClose={() => setOpenError(false)} message={errorMessage} />
      <SuccessSnackbar open={openSuccess} onClose={() => setOpenSuccess(false)} message="El cuestionario ha sido creado exitosamente." />

      {/* Modal para crear cuestionario por archivo de Excel*/}
      <BulkLoadModal
        open={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        onSelectFile={async (file) => {
          const result = await uploadExcel(file);
          if (!result?.errorMessage) setOpenSuccess(true);
          else setOpenError(true);
          return result;
        }}
        title="Crear cuestionario por Excel"
        description="Puede descargar la plantilla requerida para la creación del cuestionario y cargarla desde su equipo."
        template={{ url: TEMPLATE_QUESTIONNAIRE_URL, label: "Descargar plantilla" }}
        accept=".xlsx"
        maxSizeMB={10}
        uploadButtonLabel="Subir y crear"
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={['Las preguntas de "Selección múltiple" solo pueden tener máximo 6 opciones de respuesta.']}
        closeOnSuccess={true}
      />
      {/* Modal para crear cuestionario por archivo de Excel con preguntas de banco*/}
      <BulkLoadModal
        open={showExcelQuestionBankModal}
        onClose={() => setShowExcelQuestionBankModal(false)}
        onSelectFile={async (file) => {
          const result = await uploadExcel(file, true);
          if (result?.errorMessage) setOpenError(true);
          else {
            setImportResult(result.data);
            setShowImportResultModal(true);
          }
        }}
        title="Crear cuestionario por Excel con preguntas de banco"
        description="Puede descargar la plantilla requerida para la creación del cuestionario y cargarla desde su equipo."
        template={{ url: TEMPLATE_QUESTIONNAIRE_BANK_URL, label: "Descargar plantilla" }}
        accept=".xlsx"
        maxSizeMB={10}
        uploadButtonLabel="Subir y crear"
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={["El código de las preguntas debe coincidir con el código de las preguntas existentes en el banco de preguntas."]}
        closeOnSuccess={true}
      />
      {/* Modal de resultados de importación */}
      <BulkLoadResultsModal
        open={showImportResultModal}
        onClose={() => setShowImportResultModal(false)}
        result={importResult}
        title="Resultado de importación de cuestionario"
        labels={{
          total: "Total registros",
          created: "Creados",
          updated: "Actualizados",
          failed: "Errores",
          skipped: "Sin cambios",
        }}
        keys={{
          total: "total",
          created: "created",
          updated: "updated",
          failed: "failed",
          errorDetails: "error_details",
          skipped: "skipped",
        }}
        parseErrorMessage={parseBackendErrorMessage}
        renderErrorItem={(error, idx) => (
          <Typography key={idx} variant="body2" sx={{ p: 1, borderRadius: 1, bgcolor: idx % 2 === 0 ? "action.hover" : "transparent", mb: 0.5 }}>
            {error}
          </Typography>
        )}
      />

      {/* Modal carga masiva */}
      <BulkLoadModal
        open={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        isLoading={loadingBulk}
        onSelectFile={async (file) => {
          const result = await uploadBulkExcel(file);
          if (result?.errorMessage) {
            setErrorMessage(result.errorMessage.message || "Error en la carga masiva.");
            setOpenError(true);
          } else {
            setBulkResult(result.data);
            setShowBulkModal(false);
            setShowBulkResultModal(true);
          }
        }}
        title="Carga en lote de cuestionarios"
        description="Suba el archivo que contiene la estructura de múltiples cuestionarios."
        template={{ url: TEMPLATE_BULK_URL, label: "Descargar plantilla" }}
        accept=".xlsx"
        maxSizeMB={50}
        uploadButtonLabel="Subir y crear todos"
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={[
          "El archivo debe contener múltiples cuestionarios en el formato de banco de preguntas.",
          "Los cuestionarios con errores no se crearán, pero los demás sí.",
        ]}
        closeOnSuccess={false}
      />

      {/* Modal resultados carga masiva */}
      <BulkLoadResultsModal
        open={showBulkResultModal}
        onClose={() => setShowBulkResultModal(false)}
        result={bulkResult}
        title="Resultado de carga masiva"
        labels={{
          total: "Total cuestionarios",
          created: "Creados",
          failed: "Con errores",
        }}
        keys={{
          total: "total",
          created: "created",
          failed: "failed",
          errorDetails: "error_details",
        }}
        parseErrorMessage={(val) => {
          if (typeof val === "string") return val;
          if (val?.errors) return Array.isArray(val.errors) ? val.errors.join("; ") : val.errors;
          if (val?.error) return val.error;
          return JSON.stringify(val);
        }}
      />
    </Box>
  );
};

export default QuestionnaireCreatePage;
